// src/services/enturService.js
import { decodePolyline } from '@/utils/polyline'
/**
 * Service for interacting with Entur's APIs
 * Documentation:
 * - Geocoder API: https://developer.entur.org/pages-geocoder-api
 * - Journey Planner API: https://developer.entur.org/pages-journeyplanner-journeyplanner
 */
export default {
	/**
	 * Search for stop places using Entur's geocoder API
	 * @param {string} query - The search query
	 * @param {Object} options - Optional parameters
	 * @param {number} options.size - Number of results to return (default: 10)
	 * @param {string} options.lang - Language for results (default: 'no')
	 * @returns {Promise<Array>} - List of stop places matching the query
	 */
	async searchStopPlaces(query, options = {}) {
		if (!query || query.length < 2) return []
		const size = options.size || 10
		const lang = options.lang || 'no'
		const types = options.types || ['bus', 'train', 'metro', 'tram', 'water', 'airtravel']
		try {
			const typedCategories = {
				bus: ['bus', 'onstreetBus', 'busStation', 'coachStation'],
				train: ['rail', 'railStation', 'vehicleRailInterchange'],
				metro: ['metro', 'metroStation'],
				tram: ['tram', 'tramStation', 'onstreetTram'],
				water: ['ferry', 'ferryStop', 'ferryPort', 'harbourPort'],
				airtravel: ['airport'],
			}
			const categories = types
				.map((type) => typedCategories[type])
				.flat()
				.join(',')

			// Use the autocomplete endpoint which is better for typeahead searches
			const response = await fetch(`https://api.entur.io/geocoder/v1/autocomplete?text=${encodeURIComponent(query)}&size=${size}&lang=${lang}&categories=${categories}&multiModal=child`, {
				headers: {
					'ET-Client-Name': 'kogenta-kogo-web',
				},
			})
			if (!response.ok) {
				throw new Error(`Entur API error: ${response.status}`)
			}
			const data = await response.json()
			console.log('Search stop places:', data.features)
			const results = data.features.filter((feature) => {
				// Filter out liftStation
				if (feature.properties.category && Array.isArray(feature.properties.category)) {
					return !feature.properties.category.includes('liftStation')
				}
				return true
			})

			// Function to map category to simplified format and icon
			const mapCategory = (c) => {
				var category = { type: 'place', icon: 'fa-location-dot', label: '' }

				// Map to simplified names using includes for all categories
				if (c.includes('bus') || c.includes('coach') || c.includes('onstreetBus') || c.includes('busStation') || c.includes('coachStation')) category = { type: 'bus', icon: 'fa-bus', label: 'Buss' }
				if (c.includes('rail') || c.includes('railStation') || c.includes('vehicleRailInterchange')) category = { type: 'train', icon: 'fa-train', label: 'Tog' }
				if (c.includes('metro') || c.includes('metroStation')) category = { type: 'metro', icon: 'fa-subway', label: 'T-bane' }
				if (c.includes('tram') || c.includes('tramStation') || c.includes('onstreetTram')) category = { type: 'tram', icon: 'fa-tram', label: 'Trikk' }
				if (c.includes('ferry') || c.includes('ferryStop') || c.includes('ferryPort') || c.includes('harbourPort')) category = { type: 'water', icon: 'fa-ship', label: 'Ferge' }
				if (c.includes('airport')) category = { type: 'airtravel', icon: 'fa-plane', label: 'Flyplass' }

				// Default case if none of the above match
				return category
			}

			const getDescription = (props) => {
				if (props.description && props.description.length > 0 && props.description[0] && props.description[0].nor) {
					return props.description[0].nor
				} else {
					return ''
				}
			}

			// Transform the response to a simpler format focusing on public transport stops
			const mappedResults = results.map((feature) => {
				const categoryMapping = mapCategory(feature.properties.category)
				return {
					id: feature.properties.id,
					label: feature.properties.label,
					name: feature.properties.name,
					locality: feature.properties.locality || '',
					description: getDescription(feature.properties),
					county: feature.properties.county || '',
					category: categoryMapping,
					coordinates: feature.geometry.coordinates,
					latitude: feature.geometry.coordinates[1],
					longitude: feature.geometry.coordinates[0],
					source: feature.properties.sourceId || '',
					raw: feature.properties,
				}
			})
			console.log('searchStopPlaces', mappedResults)
			return mappedResults
		} catch (error) {
			console.error('Error searchStopPlaces:', error)
			return []
		}
	},

	/**
	 * Get details for a specific stop place
	 * @param {string} id - The stop place ID
	 * @returns {Promise<Object>} - Stop place details
	 */
	async getStopPlaceDetails(id) {
		if (!id) return null

		try {
			const response = await fetch(`https://api.entur.io/geocoder/v1/features/${id}`, {
				headers: {
					'ET-Client-Name': 'kogenta-kogo-web',
				},
			})

			if (!response.ok) {
				throw new Error(`Entur API error: ${response.status}`)
			}

			const data = await response.json()
			return {
				id: data.properties.id,
				name: data.properties.name,
				locality: data.properties.locality || '',
				county: data.properties.county || '',
				category: data.properties.category || '',
				coordinates: data.geometry.coordinates,
				latitude: data.geometry.coordinates[1],
				longitude: data.geometry.coordinates[0],
				source: data.properties.sourceId || '',
			}
		} catch (error) {
			console.error('Error getting stop place details:', error)
			return null
		}
	},

	/**
	 * Search for features (addresses, POIs, stops) around a location
	 * @param {Object} coordinates - The coordinates {latitude, longitude}
	 * @param {number} radius - Search radius in meters (default: 100)
	 * @param {string} layers - Comma-separated list of layers to include (default: 'venue')
	 * @returns {Promise<Array>} - List of features near the location
	 */
	async searchNearby(coordinates, radius = 100, layers = 'venue') {
		if (!coordinates || !coordinates.latitude || !coordinates.longitude) return []

		try {
			const response = await fetch(`https://api.entur.io/geocoder/v1/reverse?point.lat=${coordinates.latitude}&point.lon=${coordinates.longitude}&boundary.circle.radius=${radius}&size=10&layers=${layers}`, {
				headers: {
					'ET-Client-Name': 'kogenta-kogo-web',
				},
			})

			if (!response.ok) {
				throw new Error(`Entur API error: ${response.status}`)
			}

			const data = await response.json()

			return data.features.map((feature) => ({
				id: feature.properties.id,
				name: feature.properties.name,
				locality: feature.properties.locality || '',
				county: feature.properties.county || '',
				category: feature.properties.category || '',
				coordinates: feature.geometry.coordinates,
				latitude: feature.geometry.coordinates[1],
				longitude: feature.geometry.coordinates[0],
				distance: feature.properties.distance,
			}))
		} catch (error) {
			console.error('Error searching nearby:', error)
			return []
		}
	},

	/**
	 * Get all lines serving a specific stop place
	 * @param {string} stopPlaceId - The stop place ID
	 * @param {string} transportMode - Optional transport mode filter (bus, tram, metro, train, water)
	 * @returns {Promise<Array>} - List of lines serving the stop place
	 */
	async getLinesForStopPlace(stopPlaceId, destinationStopId = null, filterByTransportMode = null) {
		if (!stopPlaceId) return []

		try {
			// Create a date string for "tomorrow" to ensure we get results
			const startTime = new Date()
			startTime.setHours(0, 1, 0, 0) // 00:01
			const startTimeStr = startTime.toISOString()

			const graphqlQuery = {
				query: `{
					stopPlace(id: "${stopPlaceId}") {
						estimatedCalls(
							startTime: "${startTimeStr}",
							timeRange: 86400,
							numberOfDepartures: 100,
							includeCancelledTrips: true,
							numberOfDeparturesPerLineAndDestinationDisplay: 1
						) {
							destinationDisplay {
								frontText
							}
							serviceJourney {
								journeyPattern {
									line {
										id
										publicCode
										name
										transportMode
									}
								}
							}
						}
					}
				}`,
			}

			const response = await fetch('https://api.entur.io/journey-planner/v3/graphql', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'ET-Client-Name': 'kogenta-kogo-web',
				},
				body: JSON.stringify(graphqlQuery),
			})

			if (!response.ok) {
				throw new Error(`Entur API error: ${response.status}`)
			}

			const data = await response.json()

			if (data.errors) {
				console.error('GraphQL errors:', data.errors)
				return []
			}

			// Extract unique lines with their destinations
			const linesMap = new Map()

			console.log('getLinesForStopPlace', data, filterByTransportMode)

			data.data?.stopPlace?.estimatedCalls?.forEach((call) => {
				const line = call.serviceJourney?.journeyPattern?.line
				const destination = call.destinationDisplay?.frontText || ''

				if (line && !linesMap.has(line.id)) {
					let transportModeValue = line.transportMode?.toLowerCase() || ''
					transportModeValue = transportModeValue.replace('coach', 'bus').replace('rail', 'train')

					// Filter by transport mode if specified
					if (filterByTransportMode && transportModeValue.toLowerCase() !== filterByTransportMode.toLowerCase()) {
						console.log('Fileted away estimatedCall', call, line.transportMode, filterByTransportMode)
						return
					}

					// Filter by destinationStopId if specified
					// if (destinationStopId && destinationStopId !== destination) {
					// 	return
					// }

					// Create a unique key combining line ID and destination
					const lineDestKey = `${line.id}_${destination}`

					linesMap.set(lineDestKey, {
						id: line.id,
						name: line.publicCode || line.name,
						fullName: line.name,
						destination: destination,
						transportMode: transportModeValue,
						compositeId: lineDestKey,
					})
				}
			})

			console.log('getLinesForStopPlace result', Array.from(linesMap.values()))

			return Array.from(linesMap.values())
		} catch (error) {
			console.error('Error getting lines for stop place:', error)
			return []
		}
	},

	//originStopId
	/**
	 * Get all stops served by a specific line
	 * @param {string} lineId - The line ID
	 * @param {string} stopPlaceId - Optional stop place ID filter
	 * @returns {Promise<Array>} - List of stops served by the line
	 */
	async getStopsForLine(lineId, stopPlaceId, filterStops = true) {
		if (!lineId) return []
		try {
			const graphqlQuery = {
				query: `{
					line(id: "${lineId}") {
						id
						journeyPatterns {
							id
							directionType
							name
							quays {
								id
								name
								latitude
								longitude
								stopPlace {
									id
									name
									latitude
									longitude
								}
							}
						}
					}
				}`,
			}
			const response = await fetch('https://api.entur.io/journey-planner/v3/graphql', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'ET-Client-Name': 'kogenta-kogo-web',
				},
				body: JSON.stringify(graphqlQuery),
			})
			if (!response.ok) {
				throw new Error(`Entur API error: ${response.status}`)
			}
			const data = await response.json()
			if (data.errors) {
				console.error('GraphQL errors:', data.errors)
				return []
			}

			// Extract stops from journey patterns that include the specified stopPlaceId
			const stopsMap = new Map()
			window.patters = data.data?.line?.journeyPatterns
			const filteredJourneyPatterns = stopPlaceId ? data.data?.line?.journeyPatterns?.filter((pattern) => pattern.quays?.some((quay) => quay.stopPlace?.id === stopPlaceId)) : data.data?.line?.journeyPatterns

			filteredJourneyPatterns.forEach((pattern) => {
				let includeStops = !filterStops || !stopPlaceId

				pattern.quays?.forEach((quay) => {
					const stopPlace = quay.stopPlace

					// If we find the specified stop, start including stops after it
					if (!includeStops && stopPlace?.id === stopPlaceId) {
						includeStops = true
						return // Skip the specified stop itself
					}

					if (includeStops && stopPlace && !stopsMap.has(stopPlace.id)) {
						stopsMap.set(stopPlace.id, {
							id: stopPlace.id,
							name: stopPlace.name,
							latitude: stopPlace.latitude,
							longitude: stopPlace.longitude,
							stopName: stopPlace.name,
							stopId: stopPlace.id,
							quayName: quay.name,
							quayId: quay.id,
							quayDescription: quay.description,
							quayPublicCode: quay.publicCode,
						})
					}
				})
			})

			return Array.from(stopsMap.values())
		} catch (error) {
			console.error('Error getting stops for line:', error)
			return []
		}
	},

	/**
	 * Search for address
	 * @param {string} query - The search query
	 * @returns {Promise<Array>} - List of addresses matching the query
	 */
	async searchAddress(query) {
		if (!query || query.length < 2) return []

		try {
			const response = await fetch(`https://api.entur.io/geocoder/v1/autocomplete?text=${encodeURIComponent(query)}&size=10&lang=no&layers=address`, {
				headers: {
					'ET-Client-Name': 'kogenta-kogo-web',
				},
			})

			if (!response.ok) {
				throw new Error(`Entur API error: ${response.status}`)
			}

			const data = await response.json()

			return data.features.map((feature) => ({
				id: feature.properties.id,
				name: feature.properties.name,
				street: feature.properties.street || '',
				housenumber: feature.properties.housenumber || '',
				postalcode: feature.properties.postalcode || '',
				locality: feature.properties.locality || '',
				county: feature.properties.county || '',
				category: feature.properties.category || '',
				coordinates: feature.geometry.coordinates,
				latitude: feature.geometry.coordinates[1],
				longitude: feature.geometry.coordinates[0],
			}))
		} catch (error) {
			console.error('Error searching addresses:', error)
			return []
		}
	},

	/**
	 * Check if there is public transport between two locations
	 * @param {Object} fromCoordinates - The starting coordinates {latitude, longitude}
	 * @param {Object} toCoordinates - The destination coordinates {latitude, longitude}
	 * @param {Object} options - Optional parameters
	 * @param {string} options.transportMode - Preferred transport mode (bus, tram, rail, metro, water, air)
	 * @returns {Promise<Object>} - Connection details if public transport exists, or null
	 */
	async checkPublicTransport(fromCoordinates, toCoordinates, options = {}) {
		if (!fromCoordinates || !toCoordinates || !fromCoordinates.latitude || !fromCoordinates.longitude || !toCoordinates.latitude || !toCoordinates.longitude) {
			console.error('Invalid coordinates provided for public transport check')
			return null
		}

		try {
			// Build the GraphQL query - requesting only one trip pattern
			// Build the GraphQL query - requesting only one trip pattern
			const graphqlQuery = {
				query: `{
			trip(
			  from: {
				 coordinates: {
					latitude: ${fromCoordinates.latitude}
					longitude: ${fromCoordinates.longitude}
				 }
			  },
			  to: {
				 coordinates: {
					latitude: ${toCoordinates.latitude}
					longitude: ${toCoordinates.longitude}
				 }
			  }
			  numTripPatterns: 1
			) {
			  tripPatterns {
				 duration
				 distance
				 walkDistance
				 legs {
					mode
					distance
					line {
					  id
					  publicCode
					  transportMode
					}
					intermediateQuays {
					  id
					  name
					  latitude
					  longitude
					}
					fromEstimatedCall {
					  destinationDisplay {
						 frontText
					  }
					  quay {
						 id
						 name
						 latitude
						 longitude
						 publicCode
						 stopPlace {
							id
							name
						 }
					  }
					}
					toEstimatedCall {
					  destinationDisplay {
						 frontText
					  }
					  quay {
						 id
						 name
						 latitude
						 longitude
						 publicCode
						 stopPlace {
							id
							name
						 }
					  }
					}
				 }
			  }
			}
		 }`,
			}

			const response = await fetch('https://api.entur.io/journey-planner/v3/graphql', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'ET-Client-Name': 'kogenta-kogo-web',
				},
				body: JSON.stringify(graphqlQuery),
			})

			if (!response.ok) {
				throw new Error(`Entur API error: ${response.status}`)
			}

			const data = await response.json()

			if (data.errors) {
				console.error('GraphQL errors:', data.errors)
				return null
			}

			const tripPatterns = data.data?.trip?.tripPatterns || []

			// If no trip patterns found, return null
			if (tripPatterns.length === 0) {
				return {
					hasPublicTransport: false,
				}
			}

			// Find public transport legs (not walking)
			const publicTransportLegs = tripPatterns[0].legs.filter((leg) => leg.mode !== 'foot' && leg.line && leg.fromEstimatedCall && leg.toEstimatedCall)

			console.error('Public transport legs:', publicTransportLegs)

			// If no public transport legs found, return false
			if (publicTransportLegs.length === 0) {
				return {
					hasPublicTransport: false,
				}
			}

			// If transportMode is specified, try to find a matching leg
			let publicTransportLeg

			if (options.transportMode) {
				// Map transportMode to line.transportMode values used by Entur
				const modeMap = {
					bus: ['bus', 'coach'],
					tram: ['tram'],
					train: ['rail'],
					metro: ['metro'],
					water: ['water', 'ferry'],
					air: ['air'],
				}

				// Get the array of matching Entur transport modes
				const matchingModes = modeMap[options.transportMode.toLowerCase()] || []

				// Try to find a leg with matching transport mode
				publicTransportLeg = publicTransportLegs.find((leg) => matchingModes.includes(leg.line.transportMode.toLowerCase()))
			}

			// If no public transport leg found, return false
			if (!publicTransportLeg) {
				return {
					hasPublicTransport: false,
				}
			}

			// Return essential connection information
			return {
				hasPublicTransport: true,
				line: {
					id: publicTransportLeg.line?.id,
					code: publicTransportLeg.line?.publicCode,
					transportMode: publicTransportLeg.line?.transportMode?.replace('coach', 'bus').replace('rail', 'train') ?? 'automotive',
				},
				originStop: {
					id: publicTransportLeg.fromEstimatedCall.quay.stopPlace?.id,
					name: publicTransportLeg.fromEstimatedCall.quay.stopPlace?.name,
					latitude: publicTransportLeg.fromEstimatedCall.quay.latitude,
					longitude: publicTransportLeg.fromEstimatedCall.quay.longitude,
					quayId: publicTransportLeg.fromEstimatedCall.quay.id,
					quayName: publicTransportLeg.fromEstimatedCall.quay.name,
					quayCode: publicTransportLeg.fromEstimatedCall.quay.publicCode,
				},
				destinationStop: {
					id: publicTransportLeg.toEstimatedCall.quay.stopPlace?.id,
					name: publicTransportLeg.toEstimatedCall.quay.stopPlace?.name,
					latitude: publicTransportLeg.toEstimatedCall.quay.latitude,
					longitude: publicTransportLeg.toEstimatedCall.quay.longitude,
					quayId: publicTransportLeg.toEstimatedCall.quay.id,
					quayName: publicTransportLeg.toEstimatedCall.quay.name,
					quayCode: publicTransportLeg.toEstimatedCall.quay.publicCode,
				},
				intermediateQuays:
					publicTransportLeg.intermediateQuays?.map((quay) => ({
						id: quay.id,
						name: quay.name,
						latitude: quay.latitude,
						longitude: quay.longitude,
					})) || [],
				//locations: decodePolyline(publicTransportLeg.pointsOnLink?.points) || [],
				destination: publicTransportLeg.fromEstimatedCall.destinationDisplay?.frontText || null,
				duration: tripPatterns[0].duration,
				distance: tripPatterns[0].distance,
				walkDistance: tripPatterns[0].walkDistance,
				transportMode: publicTransportLeg.line?.transportMode?.replace('coach', 'bus').replace('rail', 'train') ?? 'automotive',
			}
		} catch (error) {
			console.error('Error checking public transport:', error)
			return null
		}
	},

	/**
	 * Check if there is public transport between two stop places
	 * @param {string} fromStopPlaceId - The origin stop place ID
	 * @param {string} toStopPlaceId - The destination stop place ID
	 * @param {Object} options - Optional parameters
	 * @param {string} options.transportMode - Preferred transport mode (bus, tram, rail, metro, water, air)
	 * @returns {Promise<Object>} - Connection details if public transport exists, or null
	 */
	async checkPublicTransportByStopPlaces(fromStopPlaceId, toStopPlaceId, options = {}) {
		if (!fromStopPlaceId || !toStopPlaceId) {
			console.error('Invalid stop place IDs provided for public transport check')
			return null
		}

		// TODO: Consider adding this
		// legs {
		// 	pointsOnLink {
		// 		points
		// 	}
		// }

		try {
			// Build the GraphQL query - requesting only one trip pattern
			const graphqlQuery = {
				query: `{
			trip(
			  from: {
				 place: "${fromStopPlaceId}"
			  },
			  to: {
				 place: "${toStopPlaceId}"
			  }
			  numTripPatterns: 1
			) {
			  tripPatterns {
				 duration
				 distance
				 walkDistance

				 legs {
					mode
					distance
					line {
					  id
					  publicCode
					  transportMode
					}
					intermediateQuays {
					  id
					  name
					  latitude
					  longitude
					}
					fromEstimatedCall {
					  destinationDisplay {
						 frontText
					  }
					  quay {
						 id
						 name
						 latitude
						 longitude
						 publicCode
						 stopPlace {
							id
							name
						 }
					  }
					}
					toEstimatedCall {
					  destinationDisplay {
						 frontText
					  }
					  quay {
						 id
						 name
						 latitude
						 longitude
						 publicCode
						 stopPlace {
							id
							name
						 }
					  }
					}
				 }
			  }
			}
		 }`,
			}

			const response = await fetch('https://api.entur.io/journey-planner/v3/graphql', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'ET-Client-Name': 'kogenta-kogo-web',
				},
				body: JSON.stringify(graphqlQuery),
			})

			if (!response.ok) {
				throw new Error(`Entur API error: ${response.status}`)
			}

			const data = await response.json()

			if (data.errors) {
				console.error('GraphQL errors:', data.errors)
				return null
			}

			const tripPatterns = data.data?.trip?.tripPatterns || []

			// If no trip patterns found, return null
			if (tripPatterns.length === 0) {
				return {
					hasPublicTransport: false,
				}
			}

			// Find public transport legs (not walking)
			const publicTransportLegs = tripPatterns[0].legs.filter((leg) => leg.mode !== 'foot' && leg.line && leg.fromEstimatedCall && leg.toEstimatedCall)

			// If no public transport legs found, return false
			if (publicTransportLegs.length === 0) {
				return {
					hasPublicTransport: false,
				}
			}

			// If transportMode is specified, try to find a matching leg
			let publicTransportLeg

			if (options.transportMode) {
				// Map transportMode to line.transportMode values used by Entur
				const modeMap = {
					bus: ['bus', 'coach'],
					tram: ['tram'],
					rail: ['rail'],
					metro: ['metro'],
					water: ['water', 'ferry'],
					air: ['air'],
				}

				// Get the array of matching Entur transport modes
				const matchingModes = modeMap[options.transportMode.toLowerCase()] || []

				// Try to find a leg with matching transport mode
				publicTransportLeg = publicTransportLegs.find((leg) => matchingModes.includes(leg.line.transportMode.toLowerCase()))
			}

			// If no matching leg found or no transportMode specified, use the first public transport leg
			if (!publicTransportLeg) {
				publicTransportLeg = publicTransportLegs[0]
			}

			// Return essential connection information
			return {
				hasPublicTransport: true,
				line: {
					id: publicTransportLeg.line?.id,
					code: publicTransportLeg.line?.publicCode,
					transportMode: publicTransportLeg.line?.transportMode,
				},
				originStop: {
					id: publicTransportLeg.fromEstimatedCall.quay.stopPlace?.id,
					name: publicTransportLeg.fromEstimatedCall.quay.stopPlace?.name,
					latitude: publicTransportLeg.fromEstimatedCall.quay.latitude,
					longitude: publicTransportLeg.fromEstimatedCall.quay.longitude,
					quayId: publicTransportLeg.fromEstimatedCall.quay.id,
					quayName: publicTransportLeg.fromEstimatedCall.quay.name,
					quayCode: publicTransportLeg.fromEstimatedCall.quay.publicCode,
				},
				destinationStop: {
					id: publicTransportLeg.toEstimatedCall.quay.stopPlace?.id,
					name: publicTransportLeg.toEstimatedCall.quay.stopPlace?.name,
					latitude: publicTransportLeg.toEstimatedCall.quay.latitude,
					longitude: publicTransportLeg.toEstimatedCall.quay.longitude,
					quayId: publicTransportLeg.toEstimatedCall.quay.id,
					quayName: publicTransportLeg.toEstimatedCall.quay.name,
					quayCode: publicTransportLeg.toEstimatedCall.quay.publicCode,
				},
				intermediateQuays:
					publicTransportLeg.intermediateQuays?.map((quay) => ({
						id: quay.id,
						name: quay.name,
						latitude: quay.latitude,
						longitude: quay.longitude,
					})) || [],
				destination: publicTransportLeg.fromEstimatedCall.destinationDisplay?.frontText || null,
				duration: tripPatterns[0].duration,
				distance: tripPatterns[0].distance,
				//locations: decodePolyline(publicTransportLeg.pointsOnLink?.points) || [],
				walkDistance: tripPatterns[0].walkDistance,
				transportMode: publicTransportLeg.line.transportMode,
			}
		} catch (error) {
			console.error('Error checking public transport between stop places:', error)
			return null
		}
	},
}
